import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * The sky over a map, drawn on a screen-sized canvas over the map about 30 times a second (every frame while the
 * view or the camera's lean is moving). It stops while the tab is hidden.
 *
 * Depth (parallax): the map is the ground and the camera looks down on it from above, the map's zoom being how low
 * it is. Each sky layer floats at its own height, so it is nearer the camera than the ground: drawn larger, and moved
 * further by a pan, the higher it is; a cloud's shadow stays on the ground under it, so the two part as the map
 * moves. A layer at height d (a share of the camera's height at zoom 1) is drawn s = 1 / (1 - d * zoom) times the
 * ground's scale, round the middle of the view. So zooming in brings the camera down through the sky: the high layers
 * grow faster than the ground and thin out as the camera passes them. The pointer leans the camera a little (towards
 * where it is over the map), shifting each layer by its height. Every layer is sized for the default zoom (0.85), so
 * there the clouds look as they did before the depth was added.
 *
 * Clouds: seamless textures built once from Braytech's own cloud photos (the shapes the owner liked), on one wind per
 * map; the textures repeat with no seam, so the clouds carry on past the map's edges and nothing appears or vanishes.
 * Space (the Moon, airless): faint dust and a few lit, cratered rocks at several heights, and satellites crossing, the
 * nearer ones larger and quicker, with solar panels.
 */

type View = { x: number; y: number; k: number };

const TEX = 1024;     // the texture's size: room for the cloud photos' detail; one repeat covers 2700 to 4200 map px
const K0 = 0.85;      // the zoom the layers are sized at (the map's first look)
const LEAN = 90;      // screen px a layer one ground-scale nearer shifts with the pointer at the view's edge
const scaleAt = (d: number, k: number) => 1 / (1 - d * Math.min(k, 2.5));
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
// a layer thins out as the camera comes down through it: by how much larger it has grown than at the default zoom
const passing = (grown: number) => 1 - smooth(1.45, 2.6, grown);

// Braytech's own cloud photos (the shapes the owner liked before any of this): the texture is built from them
const PHOTOS = ['01A3-0394', '01A3-0396', '01A3-0399', '01A3-039A', '01A3-039D', '01A3-05F6'];
let photosP: Promise<HTMLImageElement[]> | undefined;
const loadPhotos = () => (photosP ??= Promise.all(PHOTOS.map((n) => new Promise<HTMLImageElement | null>((done) => {
  const im = new Image(); im.onload = () => done(im); im.onerror = () => done(null); im.src = `/maps/atmosphere/${n}.png`;
}))).then((l) => l.filter((x): x is HTMLImageElement => !!x)));

const random = (seed: number) => { let s = seed % 2147483647 || 1; return () => ((s = (s * 16807) % 2147483647) / 2147483647); };

/**
 * A cloud texture that tiles, made from Braytech's cloud photos: they are scattered over it at random sizes, turns and
 * strengths, each softly faded at its edges, and every one that crosses an edge is drawn again on the other side, so
 * the texture repeats with no seam. The shadow version is the same, in black.
 */
function makeTexture(photos: HTMLImageElement[], seed: number, shadow: boolean, count: number): HTMLCanvasElement {
  const rnd = random(seed);
  const c = document.createElement('canvas'); c.width = c.height = TEX;
  const g = c.getContext('2d')!;
  // one soft sprite per photo: the photo with its edges faded (and blacked for the shadow)
  const sprites = photos.map((im) => {
    const sp = document.createElement('canvas'); sp.width = im.naturalWidth; sp.height = im.naturalHeight;
    const sg = sp.getContext('2d')!;
    sg.drawImage(im, 0, 0);
    const fade = sg.createRadialGradient(sp.width / 2, sp.height / 2, 0, sp.width / 2, sp.height / 2, Math.max(sp.width, sp.height) / 2);
    fade.addColorStop(0, '#000'); fade.addColorStop(0.55, 'rgba(0,0,0,0.85)'); fade.addColorStop(1, 'rgba(0,0,0,0)');
    sg.globalCompositeOperation = 'destination-in'; sg.fillStyle = fade; sg.fillRect(0, 0, sp.width, sp.height);
    if (shadow) { sg.globalCompositeOperation = 'source-in'; sg.fillStyle = '#000'; sg.fillRect(0, 0, sp.width, sp.height); }
    return sp;
  });
  for (let i = 0; i < count; i++) {
    const sp = sprites[Math.floor(rnd() * sprites.length)];
    const w = TEX * (0.45 + rnd() * 0.5), h = w * (sp.height / sp.width);
    const x = rnd() * TEX, y = rnd() * TEX, turn = (rnd() - 0.5) * 0.6, flip = rnd() < 0.5 ? -1 : 1;
    g.globalAlpha = 0.55 + rnd() * 0.45;
    // drawn at its place and wrapped round every edge it crosses, so the repeat has no seam
    for (const ox of [-TEX, 0, TEX]) for (const oy of [-TEX, 0, TEX]) {
      const cx = x + ox, cy = y + oy;
      if (cx + w < -TEX * 0.1 || cx - w > TEX * 1.1 || cy + h < -TEX * 0.1 || cy - h > TEX * 1.1) continue;
      g.save(); g.translate(cx, cy); g.rotate(turn); g.scale(flip, 1); g.drawImage(sp, -w / 2, -h / 2, w, h); g.restore();
    }
  }
  g.globalAlpha = 1;
  return c;
}

/** Space dust that tiles: tiny specks of different brightness, a few with a soft glow. */
function makeDust(seed: number, count: number): HTMLCanvasElement {
  const rnd = random(seed);
  const c = document.createElement('canvas'); c.width = c.height = TEX;
  const g = c.getContext('2d')!;
  for (let i = 0; i < count; i++) {
    const x = rnd() * TEX, y = rnd() * TEX, r = 0.5 + rnd() * rnd() * 1.6, a = 0.25 + rnd() * 0.6;
    g.fillStyle = `rgba(${215 + rnd() * 30 | 0},${215 + rnd() * 25 | 0},${225 + rnd() * 30 | 0},${a})`;
    for (const ox of [-TEX, 0, TEX]) for (const oy of [-TEX, 0, TEX]) { g.beginPath(); g.arc(x + ox, y + oy, r, 0, Math.PI * 2); g.fill(); }
  }
  return c;
}

/**
 * One rock, made once: a height field, lit. An ellipsoid body with a lumpy outline, roughened at several scales and
 * pitted with craters (a hollow with a raised rim); each pixel is lit by the slope of the surface there, from the
 * upper left (where the satellites' sun is), so the hollows and rims shade themselves and the night side falls to
 * black. Dark, slightly brown grey, as real asteroids are.
 */
function makeRock(seed: number): HTMLCanvasElement {
  const rnd = random(seed);
  const S = 160, R = S * 0.4, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d')!;
  // value noise on a grid of random numbers, smoothly blended, in octaves
  const N = 64, grid = Float32Array.from({ length: N * N }, rnd);
  const at = (i: number, j: number) => grid[((j % N) + N) % N * N + ((i % N) + N) % N];
  const noise = (x: number, y: number) => {
    const i = Math.floor(x), j = Math.floor(y), fx = x - i, fy = y - j, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
    return (at(i, j) * (1 - u) + at(i + 1, j) * u) * (1 - v) + (at(i, j + 1) * (1 - u) + at(i + 1, j + 1) * u) * v;
  };
  const fbm = (x: number, y: number) => { let s = 0, a = 0.5, f = 1; for (let o = 0; o < 5; o++) { s += a * noise(x * f, y * f); a *= 0.5; f *= 2.03; } return s; };
  // the outline: a stretched, turned ellipse whose radius wanders with the angle
  const stretch = 0.65 + rnd() * 0.3, turn = rnd() * Math.PI, ph = [rnd(), rnd(), rnd(), rnd()].map((p) => p * 6.283);
  const edge = (a: number) => 1 + 0.12 * Math.sin(2 * a + ph[0]) + 0.08 * Math.sin(3 * a + ph[1]) + 0.05 * Math.sin(5 * a + ph[2]) + 0.03 * Math.sin(9 * a + ph[3]);
  const craters = Array.from({ length: 5 + Math.floor(rnd() * 6) }, () => {
    const a = rnd() * 6.283, d = Math.sqrt(rnd()) * 0.75;
    return { x: Math.cos(a) * d, y: Math.sin(a) * d, r: 0.07 + rnd() * rnd() * 0.28 };   // in the rock's own frame
  });
  const H = new Float32Array(S * S), inside = new Float32Array(S * S);
  const cs = Math.cos(-turn), sn = Math.sin(-turn);
  for (let py = 0; py < S; py++) for (let px = 0; px < S; px++) {
    // into the rock's own frame (unturned, unstretched), in radii
    const X = (px - S / 2) / R, Y = (py - S / 2) / R;
    const x = X * cs - Y * sn, y = (X * sn + Y * cs) / stretch;
    const rr = Math.hypot(x, y), lim = edge(Math.atan2(y, x)), q = rr / lim;
    const k = py * S + px;
    inside[k] = Math.min(1, Math.max(0, (1 - q) * R * 0.9));   // soft over about one pixel
    if (q >= 1) continue;
    let h = Math.sqrt(1 - q * q) * 0.9 + (fbm(x * 3 + 7, y * 3 + 3) - 0.5) * 0.5 + (fbm(x * 9, y * 9) - 0.5) * 0.12;
    for (const cr of craters) {
      const t = Math.hypot(x - cr.x, y - cr.y) / cr.r;
      if (t < 1) h -= cr.r * 0.9 * (1 - t * t);                                  // the hollow
      h += cr.r * 0.25 * Math.exp(-((t - 1) * (t - 1)) / 0.04);                    // its raised rim
    }
    H[k] = h;
  }
  const img = g.createImageData(S, S), o = img.data;
  const L = [-0.74, -0.56, 0.38], ln = Math.hypot(L[0], L[1], L[2]);   // low from the side: a clear night side
  const lx = L[0] / ln, ly = L[1] / ln, lz = L[2] / ln;
  for (let py = 1; py < S - 1; py++) for (let px = 1; px < S - 1; px++) {
    const k = py * S + px, a = inside[k];
    if (a <= 0) continue;
    // the surface's slope here (heights are in radii, so times R for pixels), and how squarely the sun falls on it
    const dx = ((H[k + 1] - H[k - 1]) / 2) * R, dy = ((H[k + S] - H[k - S]) / 2) * R;
    const nx = -dx, ny = -dy, nz = 1, nl = Math.hypot(nx, ny, nz);
    const lit = Math.max(0, (nx * lx + ny * ly + nz * lz) / nl);
    const albedo = 0.8 + 0.4 * fbm(px * 0.11 + 31, py * 0.11 + 17);   // patches lighter and darker
    const v = (0.05 + 1.05 * Math.pow(lit, 1.15)) * albedo;
    o[k * 4] = Math.min(255, 118 * v); o[k * 4 + 1] = Math.min(255, 110 * v); o[k * 4 + 2] = Math.min(255, 101 * v); o[k * 4 + 3] = 255 * a;
  }
  g.putImageData(img, 0, 0);
  return c;
}

const textures = new Map<string, HTMLCanvasElement>();
const cached = (key: string, make: () => HTMLCanvasElement) => { if (!textures.has(key)) textures.set(key, make()); return textures.get(key)!; };

// The cloud layers: texture seed and how many photos it scatters, how many map pixels one texture repeat covers at the
// default zoom, drift speed (map px a second), an angle off the wind, strength, an offset in map pixels (the shadow sits
// downwind and below its clouds), and its height (d, see above). Low, middle and high clouds at different heights
// slide over each other as the map moves; the shadow is on the ground. The two low layers differ a little in size,
// speed, angle and height, so where they overlap the clouds grow, merge and part as they travel. The highest, sparse
// wisps are what the camera comes down through first; the high haze is a little fainter than before, to make room
// for them without more cloud over the map.
interface Layer { seed: number; count: number; shadow?: boolean; dust?: boolean; tile: number; speed: number; tilt: number; alpha: number; off: number; d: number }
const LAYERS: Layer[] = [
  { seed: 4421, count: 6, shadow: true, tile: 2700, speed: 28, tilt: 0, alpha: 0.1, off: 90, d: 0 },
  { seed: 4421, count: 6, tile: 2700, speed: 28, tilt: 0, alpha: 0.32, off: 0, d: 0.07 },
  { seed: 7717, count: 5, tile: 3100, speed: 22, tilt: 0.09, alpha: 0.26, off: 0, d: 0.11 },
  { seed: 9137, count: 7, tile: 4200, speed: 16, tilt: 0, alpha: 0.18, off: 0, d: 0.19 },
  { seed: 5309, count: 3, tile: 3600, speed: 34, tilt: -0.05, alpha: 0.1, off: 0, d: 0.29 },
];
// Space: two veils of dust (far and faint, nearer and sparser) instead of clouds
const DUST: Layer[] = [
  { seed: 1187, count: 150, dust: true, tile: 1700, speed: 3, tilt: 0.3, alpha: 0.35, off: 0, d: 0.05 },
  { seed: 6113, count: 45, dust: true, tile: 2300, speed: 6, tilt: 0.3, alpha: 0.4, off: 0, d: 0.17 },
];
// the rocks: at three heights, a few to a repeat of the field (layer px), a size range (map px at the default zoom),
// a slow drift (layer px a second) and how bright they are drawn (the far ones dimmer, so they read as far)
const ROCKS = [
  { d: 0.09, tile: 2600, n: 4, size: [14, 30], drift: 5, alpha: 0.55 },
  { d: 0.18, tile: 3400, n: 3, size: [26, 52], drift: 8, alpha: 0.75 },
  { d: 0.28, tile: 5200, n: 2, size: [50, 95], drift: 12, alpha: 0.9 },
];

/**
 * A satellite crossing the sky (the Moon): a point with a faint trail and a slow blinking light; the near ones (low
 * orbits) larger and quicker, with solar panels. Its path is in its own layer, so it keeps to the same sky while the
 * map is dragged or zoomed, with its height's parallax.
 */
type Sat = { d: number; x0: number; y0: number; vx: number; vy: number; born: number; life: number; blink: number; near: boolean };

/**
 * Placed inside the map's canvas, between the drawn art and the names and points, over exactly the part of the map in
 * view; its drawing is one canvas pixel per screen pixel. It redraws on every pan and zoom as well as on its own
 * clock, so the sky never lags behind a drag.
 */
export function CloudLayer({ view, size, windLeft, tilt, sky = 'clouds' }: { view: View; size: { w: number; h: number }; windLeft: boolean; tilt: number; sky?: 'clouds' | 'space' }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const viewNow = useRef(view); viewNow.current = view;
  const redraw = useRef<(now: number) => void>(() => undefined);
  useLayoutEffect(() => { redraw.current(performance.now()); }, [view]);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !size.w || !size.h) return;
    cv.width = Math.round(size.w); cv.height = Math.round(size.h);
    const g = cv.getContext('2d');
    if (!g) return;
    const W = cv.width, H = cv.height, cx = W / 2, cy = H / 2;
    const wx = windLeft ? -1 : 1, wy = tilt;   // the wind: sideways, a little up or down
    let pats: (Layer & { p: CanvasPattern })[] = [];
    let alive = true;
    const start = performance.now() - 300000;   // already well under way: no clouds lined up at the start

    // the camera's lean: where the pointer is over the view (-1 to 1 each way), eased
    const lean = { x: 0, y: 0, tx: 0, ty: 0 };
    const host = cv.closest('.g-mapview') as HTMLElement | null;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;   // a finger drags the map; the lean follows a mouse
      const r = host!.getBoundingClientRect();
      lean.tx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      lean.ty = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
    };
    const onLeave = () => { lean.tx = 0; lean.ty = 0; };
    host?.addEventListener('pointermove', onMove);
    host?.addEventListener('pointerleave', onLeave);

    // a layer at height d: its scale over the ground's, how much larger than at the default zoom, and where its
    // origin lands on the screen (the ground's, pushed out from the view's middle by its scale, and leaned)
    const place = (d: number, v: View) => {
      const s = scaleAt(d, v.k), s0 = scaleAt(d, K0);
      const ox = s * v.x + cx * (1 - s) - lean.x * LEAN * (s - 1), oy = s * v.y + cy * (1 - s) - lean.y * LEAN * (s - 1);
      return { s, s0, grown: s / s0, ox, oy, unit: (v.k * s) / s0 };   // unit: screen px per layer px
    };

    let lastDraw = 0;
    const draw = (now: number) => {
      const v = viewNow.current, t = (now - start) / 1000;
      const dt = lastDraw ? Math.min(100, now - lastDraw) : 0; lastDraw = now;
      const ease = 1 - Math.exp(-dt / 160);
      lean.x += (lean.tx - lean.x) * ease; lean.y += (lean.ty - lean.y) * ease;
      g.clearRect(0, 0, W, H);
      for (const l of pats) {
        const P = place(l.d, v), fade = l.d ? passing(P.grown) : 1;
        if (fade <= 0.01) continue;
        // the drift, wrapped at one repeat so the numbers stay small (the texture is seamless, so this never shows)
        const dx = ((wx * l.speed * t) % l.tile) + wx * l.off, dy = (((wy + l.tilt) * l.speed * t) % l.tile) + l.off * 0.6;
        const sc = (l.tile / TEX) * P.unit;
        l.p.setTransform(new DOMMatrix([sc, 0, 0, sc, P.ox + dx * P.unit, P.oy + dy * P.unit]));
        g.globalAlpha = l.alpha * fade;
        g.fillStyle = l.p;
        g.fillRect(0, 0, W, H);
      }
      g.globalAlpha = 1;
      if (sky === 'space') { drawRocks(t, v); drawSats(now, v); }
    };

    // ---------------- space: rocks ----------------
    const rocks = ROCKS.map((band, bi) => {
      const rnd = random(4242 + bi * 97);
      return { ...band, list: Array.from({ length: band.n }, (_, i) => ({
        x: rnd() * band.tile, y: rnd() * band.tile, size: band.size[0] + rnd() * (band.size[1] - band.size[0]),
        sprite: sky === 'space' ? cached(`rock:${bi}:${i}`, () => makeRock(7001 + bi * 31 + i * 7)) : null,
        vx: (rnd() - 0.5) * band.drift * 2, vy: (rnd() - 0.5) * band.drift * 2, flip: rnd() < 0.5,
      })) };
    });
    const drawRocks = (t: number, v: View) => {
      for (const band of rocks) {
        const P = place(band.d, v), fade = passing(P.grown);
        if (fade <= 0.01) continue;
        // the screen in this layer's px, and every repeat of the field that reaches it
        const u0 = -P.ox / P.unit, u1 = (W - P.ox) / P.unit, w0 = -P.oy / P.unit, w1 = (H - P.oy) / P.unit;
        for (const r of band.list) {
          const half = r.size, px = r.size * P.unit;   // its size: in the layer's px, and on screen
          const rx = (((r.x + r.vx * t) % band.tile) + band.tile) % band.tile, ry = (((r.y + r.vy * t) % band.tile) + band.tile) % band.tile;
          for (let ax = Math.floor((u0 - rx - half) / band.tile); ax <= Math.floor((u1 - rx + half) / band.tile); ax++)
            for (let ay = Math.floor((w0 - ry - half) / band.tile); ay <= Math.floor((w1 - ry + half) / band.tile); ay++) {
              const sx = P.ox + (rx + ax * band.tile) * P.unit, sy = P.oy + (ry + ay * band.tile) * P.unit;
              g.globalAlpha = band.alpha * fade;
              g.save(); g.translate(sx, sy); if (r.flip) g.scale(-1, 1);
              g.drawImage(r.sprite!, -px / 2, -px / 2, px, px); g.restore();
            }
        }
      }
      g.globalAlpha = 1;
    };

    // ---------------- space: satellites ----------------
    // one now and then, every 8 to 20 s, sometimes a second close behind; about one in three in a low orbit (near)
    const sats: Sat[] = [];
    let nextSat = performance.now() + 2500 + Math.random() * 4000;
    const launch = (now: number) => {
      const v = viewNow.current, near = Math.random() < 0.35;
      const d = near ? 0.2 + Math.random() * 0.08 : 0.02 + Math.random() * 0.1;
      const P = place(d, v);
      // in from a random edge of the view, out across the far side, a little slanted; then kept in its layer's px
      const fromLeft = Math.random() < 0.5, y0 = H * (0.1 + Math.random() * 0.8), y1 = H * (0.1 + Math.random() * 0.8);
      const life = near ? 6000 + Math.random() * 3500 : 10000 + Math.random() * 10000, x0 = fromLeft ? -60 : W + 60, x1 = fromLeft ? W + 60 : -60;
      const lx = (sx: number) => (sx - P.ox) / P.unit, ly = (sy: number) => (sy - P.oy) / P.unit;
      sats.push({ d, x0: lx(x0), y0: ly(y0), vx: (lx(x1) - lx(x0)) / life, vy: (ly(y1) - ly(y0)) / life, born: now, life, blink: Math.random() * 1400, near });
    };
    const drawSats = (now: number, v: View) => {
      if (now > nextSat) {
        launch(now);
        nextSat = now + (Math.random() < 0.2 ? 2000 + Math.random() * 3000 : 8000 + Math.random() * 12000);
      }
      for (let i = sats.length - 1; i >= 0; i--) {
        const st = sats[i], age = now - st.born;
        if (age > st.life) { sats.splice(i, 1); continue; }
        const P = place(st.d, v);
        const x = P.ox + (st.x0 + st.vx * age) * P.unit, y = P.oy + (st.y0 + st.vy * age) * P.unit;
        const fade = Math.min(1, age / 1200, (st.life - age) / 1200) * passing(P.grown);
        if (fade <= 0.01) continue;
        const z = st.near ? 2.2 * P.grown : 1;   // a near one is drawn larger, and larger still as the camera comes down
        const sp = Math.hypot(st.vx, st.vy) || 1, ux = st.vx / sp, uy = st.vy / sp;
        // the trail: a faint line behind it
        const len = 70 * (st.near ? 1.8 : 1), tx = x - ux * len, ty = y - uy * len;
        const tr = g.createLinearGradient(tx, ty, x, y);
        tr.addColorStop(0, 'rgba(220,230,255,0)'); tr.addColorStop(1, `rgba(220,230,255,${0.45 * fade})`);
        g.strokeStyle = tr; g.lineWidth = st.near ? 2.4 : 2; g.lineCap = 'round'; g.beginPath(); g.moveTo(tx, ty); g.lineTo(x, y); g.stroke();
        if (st.near) {
          // a body with two solar panels square to its path, the sunlit side brighter
          g.save(); g.translate(x, y); g.rotate(Math.atan2(uy, ux)); g.globalAlpha = fade;
          const bw = 3.2 * z, bh = 2.2 * z, pw = 2 * z, pl = 6.5 * z;
          g.fillStyle = '#2b3d63'; g.fillRect(-pw / 2, -bh / 2 - pl - 0.6 * z, pw, pl); g.fillRect(-pw / 2, bh / 2 + 0.6 * z, pw, pl);
          g.strokeStyle = 'rgba(160,190,255,0.55)'; g.lineWidth = 0.5;
          g.strokeRect(-pw / 2, -bh / 2 - pl - 0.6 * z, pw, pl); g.strokeRect(-pw / 2, bh / 2 + 0.6 * z, pw, pl);
          g.fillStyle = '#d9d4c7'; g.fillRect(-bw / 2, -bh / 2, bw, bh);
          g.fillStyle = 'rgba(255,255,255,0.8)'; g.fillRect(-bw / 2, -bh / 2, bw, bh * 0.35);
          g.restore(); g.globalAlpha = 1;
        } else {
          // a soft halo and the body
          const halo = g.createRadialGradient(x, y, 0, x, y, 7);
          halo.addColorStop(0, `rgba(230,238,255,${0.35 * fade})`); halo.addColorStop(1, 'rgba(230,238,255,0)');
          g.fillStyle = halo; g.beginPath(); g.arc(x, y, 7, 0, Math.PI * 2); g.fill();
          g.fillStyle = `rgba(245,248,255,${0.95 * fade})`; g.beginPath(); g.arc(x, y, 2.6, 0, Math.PI * 2); g.fill();
        }
        // its light, blinking about every 1.4 s
        if ((now + st.blink) % 1400 < 200) {
          const gr = 12 * (st.near ? 1.4 : 1), glow = g.createRadialGradient(x, y, 0, x, y, gr);
          glow.addColorStop(0, `rgba(255,110,100,${0.95 * fade})`); glow.addColorStop(1, 'rgba(255,110,100,0)');
          g.fillStyle = glow; g.beginPath(); g.arc(x, y, gr, 0, Math.PI * 2); g.fill();
        }
      }
    };

    // about 30 frames a second is plenty for drifting clouds; every frame while the camera's lean is still moving
    let frame = 0, last = 0;
    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);
      const moving = Math.abs(lean.tx - lean.x) + Math.abs(lean.ty - lean.y) > 0.004;
      if (!moving && now - last < 33) return;
      last = now;
      draw(now);
    };
    const go = () => { cancelAnimationFrame(frame); if (!document.hidden) frame = requestAnimationFrame(loop); };
    const begin = () => {
      redraw.current = draw;
      draw(performance.now());   // one frame at once
      document.addEventListener('visibilitychange', go);
      go();
    };
    if (sky === 'space') {
      pats = DUST.map((l) => ({ ...l, p: g.createPattern(cached(`dust:${l.seed}:${l.count}`, () => makeDust(l.seed, l.count)), 'repeat')! }));
      begin();
    } else {
      // the photos load once; the textures are made once each and then only drawn
      loadPhotos().then((photos) => {
        if (!alive || !photos.length) return;
        pats = LAYERS.map((l) => ({ ...l, p: g.createPattern(cached(`${l.seed}:${!!l.shadow}:${l.count}`, () => makeTexture(photos, l.seed, !!l.shadow, l.count)), 'repeat')! }));
        begin();
      });
    }
    return () => {
      alive = false; redraw.current = () => undefined; cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', go);
      host?.removeEventListener('pointermove', onMove); host?.removeEventListener('pointerleave', onLeave);
    };
  }, [size.w, size.h, windLeft, tilt, sky]);
  return <canvas ref={ref} className="g-mapclouds" aria-hidden="true"
    style={{ left: -view.x / view.k, top: -view.y / view.k, width: size.w / view.k, height: size.h / view.k }} />;
}
